import { useCallback, useMemo } from 'react'
import { useTransactionAdder } from '../state/transactions/hooks'
import { useERC721Contract, useMarketPlaceContract } from './useContract'
import { calculateGasMargin } from '../utils'
import { parseEther } from '@ethersproject/units'
import { TransactionResponse } from '@ethersproject/providers'
import { useSingleCallResult } from '../state/multicall/hooks'
import { ChainId, JSBI, TokenAmount } from '@yetiswap/sdk'
import { Token } from '@yetiswap/sdk/dist/entities/token'
import { useActiveWeb3React } from './index'
import { ApprovalState } from './useApproveCallback'
import { MARKET_PLACE_ADDRESS } from '../constants'
import { ISaleTokenList, SaleTokenList } from '../pages/NFTMarketPlace/data/SaleTokenList'

export enum NFTMarketPlaceCallBackState {
  INVALID,
  LOADING,
  VALID
}

export function useBuyNFTTokenWithAvax(
  tokenIndex: number | undefined,
  price: string
): {
  buyNFTState: NFTMarketPlaceCallBackState
  buyNFTCallbackAvax: null | (() => Promise<string>)
  error: string | null
} {
  const addTransaction = useTransactionAdder()
  const contract = useMarketPlaceContract()
  return useMemo(() => {
    if (!tokenIndex || !contract) {
      return {
        buyNFTState: NFTMarketPlaceCallBackState.INVALID,
        buyNFTCallbackAvax: null,
        error: 'Missing dependencies'
      }
    }

    const args = [tokenIndex]

    return {
      buyNFTState: NFTMarketPlaceCallBackState.VALID,
      buyNFTCallbackAvax: async function onBuyNFT(): Promise<string> {
        return await contract.estimateGas['buyNFTwithAvax'](...args, { value: price }).then(estimatedGasLimit => {
          return contract
            .buyNFTwithAvax(...args, { value: price, gasLimit: calculateGasMargin(estimatedGasLimit) })
            .then((response: TransactionResponse) => {
              return response.wait(1).then((e: any) => {
                let result = 'NFT Buy Failed'
                if (e && e.status === 1) {
                  result = 'NFT Buy Successful'
                }
                addTransaction(response, {
                  summary: result
                })
              })
            })
            .catch((error: any) => {
              // if the user rejected the tx, pass this along
              if (error?.code === 4001) {
                console.error(`'Transaction rejected buyNFTCallbackAvax.'`, error, tokenIndex)
              } else {
                // otherwise, the error was unexpected and we need to convey that
                console.error(`NFT Buy failed`, error, tokenIndex)
              }
              return error
            })
        })
      },
      error: null
    }
  }, [tokenIndex, addTransaction, contract, price])
}

export function useBuyNFTToken(
  tokenIndex: number | undefined
): { buyNFTState: NFTMarketPlaceCallBackState; buyNFTCallback: null | (() => Promise<string>); error: string | null } {
  const addTransaction = useTransactionAdder()
  const contract = useMarketPlaceContract()
  return useMemo(() => {
    if (!tokenIndex || !contract) {
      return { buyNFTState: NFTMarketPlaceCallBackState.INVALID, buyNFTCallback: null, error: 'Missing dependencies' }
    }

    const args = [tokenIndex]

    return {
      buyNFTState: NFTMarketPlaceCallBackState.VALID,
      buyNFTCallback: async function onBuyNFT(): Promise<string> {
        return await contract.estimateGas['buyNFT'](...args, {}).then(estimatedGasLimit => {
          return contract
            .buyNFT(...args, { value: null, gasLimit: calculateGasMargin(estimatedGasLimit) })
            .then((response: TransactionResponse) => {
              return response.wait(1).then((e: any) => {
                let result = 'NFT Buy Failed'
                if (e && e.status === 1) {
                  result = 'NFT Buy Successful'
                }
                addTransaction(response, {
                  summary: result
                })
              })
            })
            .catch((error: any) => {
              // if the user rejected the tx, pass this along
              if (error?.code === 4001) {
                console.error(`'Transaction rejected. buyNFTCallback'`, error, tokenIndex)
              } else {
                // otherwise, the error was unexpected and we need to convey that
                console.error(`NFT Buy failed`, error, tokenIndex)
              }
              return error
            })
        })
      },
      error: null
    }
  }, [tokenIndex, addTransaction, contract])
}

export function useGetTokenUri(address: string, tokenId: number): string {
  const contract = useERC721Contract(address)
  const tokenURIResult = useSingleCallResult(contract, 'tokenURI', [tokenId])
  let result = ''

  if (!tokenURIResult.loading && tokenURIResult.result && tokenURIResult.result[0]) {
    result = tokenURIResult.result[0]
  }
  return result
}

export function useGetNFTPrice(price?: string | number, token?: Token) {
  const oneToken = JSBI.BigInt(1000000000000000000)
  let tokenPrice = '0'
  if (token && price) {
    const tokenAmount = new TokenAmount(
      token,
      JSBI.divide(JSBI.multiply(JSBI.BigInt(price), JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(18))), oneToken)
    )

    tokenPrice = tokenAmount.toSignificant(10)
  }
  return tokenPrice
}

export function usePutOnSale(
  tokenContract: string,
  tokenId: number,
  price: string,
  category: number,
  saleToken: string
): [() => Promise<void>] {
  let tokenPrice = '0'

  if (price !== '') {
    tokenPrice = parseEther(price).toString()
  }

  const contract = useMarketPlaceContract()
  const addTransaction = useTransactionAdder()

  const saleNFT = useCallback(async (): Promise<void> => {
    if (!tokenId || !contract || category === 0) return

    const args = [tokenContract, tokenId.toString(), saleToken, tokenPrice, category.toString()]

    return await contract.estimateGas['saleNFT'](...args, {}).then(estimatedGasLimit => {
      return contract
        .saleNFT(...args, { value: null, gasLimit: calculateGasMargin(estimatedGasLimit) })
        .then((response: TransactionResponse) => {
          addTransaction(response, {
            summary: 'NFT Sale'
          })
          return response.wait(1)
        })
    })
  }, [tokenContract, tokenId, category, tokenPrice, addTransaction, contract, saleToken])

  return [saleNFT]
}

export function useApproveCallbackFromNFT(tokenId: number, tokenAddress: string): [ApprovalState, () => Promise<void>] {
  const contract = useERC721Contract(tokenAddress)
  const { chainId } = useActiveWeb3React()
  const marketPlaceAddress = chainId ? MARKET_PLACE_ADDRESS[chainId] : MARKET_PLACE_ADDRESS[ChainId.FUJI]
  const addTransaction = useTransactionAdder()

  const currentAllowance = useSingleCallResult(contract, 'getApproved', [tokenId])

  const approvalState: ApprovalState =
    currentAllowance.result?.[0] === marketPlaceAddress ? ApprovalState.APPROVED : ApprovalState.NOT_APPROVED

  const approve = useCallback(async (): Promise<void> => {
    if (approvalState !== ApprovalState.NOT_APPROVED) {
      console.error('approve was called unnecessarily')
      return
    }

    if (!tokenId || !contract) return

    const args = [marketPlaceAddress, tokenId]

    return await contract.estimateGas['approve'](...args, {}).then(estimatedGasLimit => {
      return contract
        .approve(...args, { value: null, gasLimit: calculateGasMargin(estimatedGasLimit) })
        .then((response: TransactionResponse) => {
          addTransaction(response, {
            summary: 'NFT Selling Approved'
          })
          return response.wait(1)
        })
    })
  }, [tokenId, approvalState, marketPlaceAddress, addTransaction, contract])

  return [approvalState, approve]
}

export function useRemoveNFTOnSale(tokenId: number): [() => Promise<void>] {
  const contract = useMarketPlaceContract()
  const addTransaction = useTransactionAdder()

  const removeNFT = useCallback(async (): Promise<void> => {
    if (!tokenId || !contract) return

    const args = [tokenId]

    return await contract.estimateGas['removeNFT'](...args, {}).then(estimatedGasLimit => {
      return contract
        .removeNFT(...args, { value: null, gasLimit: calculateGasMargin(estimatedGasLimit) })
        .then((response: TransactionResponse) => {
          addTransaction(response, {
            summary: 'Remove NFT ON Sale'
          })
          return response.wait(1)
        })
    })
  }, [tokenId, addTransaction, contract])

  return [removeNFT]
}

export function useGetYetiswapMarketCommissionRateYTS() {
  const contract = useMarketPlaceContract()
  const commissionResult = useSingleCallResult(contract, 'comissionRateYTS', [])
  let result = ''

  if (!commissionResult.loading && commissionResult.result && commissionResult.result[0]) {
    result = JSBI.BigInt(commissionResult.result[0]).toString()
  }
  return result
}

export function useGetYetiswapMarketCommissionRateNonYTS() {
  const contract = useMarketPlaceContract()
  const commissionResult = useSingleCallResult(contract, 'comissionRateNonYTS', [])
  let result = ''

  if (!commissionResult.loading && commissionResult.result && commissionResult.result[0]) {
    result = JSBI.BigInt(commissionResult.result[0]).toString()
  }
  return result
}

export function useGetYetiswapMarketCreatorRate(address: string) {
  const contract = useMarketPlaceContract()
  const commissionResult = useSingleCallResult(contract, 'creatorRate', [address])
  let result = 0

  if (!commissionResult.loading && commissionResult.result && commissionResult.result[0]) {
    result = parseInt(JSBI.BigInt(commissionResult.result[0]).toString())
  }
  return result
}

export function useGetMarketOnSaleCreatorRate(saleIndex: number) {
  const contract = useMarketPlaceContract()
  const commissionResult = useSingleCallResult(contract, 'getCreatorRate', [saleIndex])
  let result = ''

  if (!commissionResult.loading && commissionResult.result && commissionResult.result[0]) {
    result = JSBI.BigInt(commissionResult.result[0]).toString()
  }
  return result
}

export function useGetMarketOnSaleCommissionRate(saleIndex: number) {
  const contract = useMarketPlaceContract()
  const commissionResult = useSingleCallResult(contract, 'getComissionRate', [saleIndex])
  let result = ''

  if (!commissionResult.loading && commissionResult.result && commissionResult.result[0]) {
    result = JSBI.BigInt(commissionResult.result[0]).toString()
  }
  return result
}

export function useGetSaleTokenItem(tokenAddress: string) {
  const { chainId } = useActiveWeb3React()
  let result = {
    symbol: SaleTokenList[0].name,
    image: SaleTokenList[0].image
  }
  const token = SaleTokenList.find((item: ISaleTokenList) => item[chainId ? chainId : ChainId.FUJI] === tokenAddress)

  if (token) {
    result = {
      symbol: token.name,
      image: token.image
    }
  }
  return result
}

export function useTransferNft(tokenId: number, tokenAddress: string, toAdress: string | null): [() => Promise<void>] {
  const contract = useERC721Contract(tokenAddress)
  const addTransaction = useTransactionAdder()
  const { account } = useActiveWeb3React()

  const transferNFTTo = useCallback(async (): Promise<void> => {
    if (!tokenId || !contract || !toAdress) return

    const args = [account, toAdress, tokenId]

    return await contract.estimateGas['transferFrom'](...args, {}).then(estimatedGasLimit => {
      return contract
        .transferFrom(...args, { value: null, gasLimit: calculateGasMargin(estimatedGasLimit) })
        .then((response: TransactionResponse) => {
          addTransaction(response, {
            summary: 'NFT transfered'
          })
          return response.wait(1)
        })
    })
  }, [toAdress, tokenId, addTransaction, contract, account])

  return [transferNFTTo]
}
